import { Component, ContentChild, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Optional, Output, TemplateRef, ViewChild } from '@angular/core';
import { ControlContainer, FormControl } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { isControlOptional } from '@toevla-webapplications/core';

@Component({
    selector: 'tvcore-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: false
})
export class DropdownComponent implements OnInit, OnChanges, OnDestroy {
	@ContentChild('extraOptionsTemplate')
	extraOptionsTemplate: TemplateRef<any>;
	@ContentChild('optionTemplate')
	optionTemplate: TemplateRef<any>;
	@ContentChild('selectedTemplate')
	selectedTemplate: TemplateRef<any>;

	@ViewChild('searchElement')
	searchElement: ElementRef;

	@Input()
	showClear?: boolean = false;
	@Input()
	key?: string;
	@Input()
	hideOptional: boolean = false;
	@Input()
	multi: boolean = false;
	@Input()
	options: any[] | null;
	@Input()
	placeholder: string;
	@Input()
	searchable: string[] | null = null;
	@Input()
	searchablePlaceholder: string | null;
	@Input()
	selected: any;
	@Input()
	outsideChanges: boolean = false;

	@Output()
	doSelect: EventEmitter<any> = new EventEmitter<any>();
	@Output()
	searching: EventEmitter<string> = new EventEmitter<string>();

	data: any[];
	isOpen: boolean = false;
	query: FormControl = new FormControl('');

	@Input()
	label: string;
	@Input()
	name: string;
	@Input()
	showErrors: boolean;

	control: FormControl | null;

	protected destroyed$: Subject<void> = new Subject();

	constructor(
		private elem: ElementRef,
		@Optional() private controlContainer?: ControlContainer
	) {}

	get hasValue(): boolean {
		return this.multi ? this.selected.length > 0 : !!this.selected;
	}

	get isOptional(): boolean {
		return isControlOptional(this.control);
	}

	ngOnInit() {
		this.query.valueChanges.pipe(debounceTime(400), takeUntil(this.destroyed$)).subscribe(() => this.calculateVisible());

		this.control = this.controlContainer?.control?.get(this.name) as FormControl;

		if (this.control) {
			this.selected = this.control.value;

			if (this.outsideChanges) {
				this.control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => (this.selected = value));
			}
		}
	}

	ngOnChanges() {
		this.calculateVisible();
	}

	ngOnDestroy() {
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	clear(event: any) {
		event.stopPropagation();

		if (this.multi) {
			this.selected = [];
		} else {
			this.selected = null;
		}

		this.control?.setValue(this.selected);
		this.query.setValue('');
	}

	@HostListener('document:keydown.escape')
	close() {
		this.isOpen = false;
	}

	getOptionByKey(option: any): any {
		if (this.key) {
			return this.options?.find((o) => o[this.key || ''] === option);
		}

		return option;
	}

	hasError(name: string): boolean {
		return !!this.control?.hasError(name);
	}

	hasErrors(): boolean {
		if (this.showErrors) {
			return !!this.control?.errors;
		}

		return false;
	}

	isOptionActive(option: any): boolean {
		const value = this.key ? option[this.key] : option;

		if (this.multi) {
			return this.selected.some((o: any) => o === (value ?? option));
		}

		return this.selected === value;
	}

	open() {
		this.isOpen = true;

		if (this.searchable) {
			setTimeout(() => {
				this.searchElement.nativeElement.focus();
			}, 100);
		}
	}

	@HostListener('document:click', ['$event'])
	pointer(event: PointerEvent) {
		if (!this.elem.nativeElement.contains(event.target)) {
			this.close();
		}
	}

	select(option: any) {
		const value = this.key ? option[this.key] : option;

		this.doSelect.emit(value);

		if (this.control) {
			if (this.multi) {
				if (this.isOptionActive(value)) {
					this.selected = this.selected.filter((o: any) => o !== value);
				} else {
					this.selected = [...this.selected, value];
				}
			} else {
				this.selected = value;
			}

			this.control.setValue(this.selected);
		}

		if (!this.multi) {
			this.close();
		}
	}

	toggle() {
		if (!this.control || this.control.enabled) {
			if (this.isOpen) {
				this.close();
			} else {
				this.open();
			}
		}
	}

	private calculateVisible() {
		this.data = this.options || [];

		const query = this.query.value;

		if (this.searchable) {
			if (query) {
				this.data = (this.options || []).filter((option) => {
					return this.searchable?.some((property) => {
						return this.checkString(option, property, query);
					});
				});
			}

			this.searching.emit(query);
		}
	}

	private checkString(obj: any, property: string, query: string): boolean {
		const value = property.split('.').reduce((a, b) => a[b], obj);
		return value?.toLowerCase().includes(query.toLowerCase());
	}
}
